<template>
  <div class="bg-white shadow p-10 rounded w-full mx-auto max-w-2xl">
    <div class="flex items-center justify-center">
      <div
        class="flex items-center py-2"
        v-if="company && company.logo && company.logo_url"
      >
        <img
          class="h-12 w-auto"
          @error="company.logo = false"
          style="object-fit: scale-down; min-height: 100%"
          :src="company.logo_url"
          :alt="company.name"
        />
      </div>
      <div
        v-else
        class="h-16 w-16 flex rounded-full items-center justify-center"
      >
        <img
          class="h-14 w-14"
          :src="'/images/security.svg'"
          alt=""
        />
      </div>
    </div>

    <h1 class="text-center text-lg font-bold mt-8 text-blue-900">Enter the authentication code</h1>

    <div class="max-w-md mx-auto">
      <div class="pt-2 text-gray-600 text-center max-w-md mx-auto text-base">
        Protecting your data is our top priority. Please confirm your account by entering the authentication code sent
        via {{ contact.phone_number?.mfa_enabled ? "sms" : "email" }} to
      </div>

      <div class="text-center mt-2">
        <div class="flex-grow">
          <div class="text-gray-600 text-base font-semibold">
            {{ contact.phone_number?.mfa_enabled ? getPhoneNumber() : contact.email }}
          </div>
        </div>
      </div>
    </div>

    <form
      action="#"
      @submit.prevent="submit"
    >
      <div class="flex mt-4 items-center pt-4 justify-center font-semibold">
        <div class="flex-shrink-0">
          <otp-field
            v-model="form.code"
            @paste="onPaste"
          />
        </div>
      </div>
      <div
        class="pt-4"
        v-if="form.errors.has('code')"
      >
        <p class="text-red-500 whitespace-normal text-center">
          {{ form.errors.first("code") }}
        </p>
      </div>
      <div class="flex my-8 items-center justify-center text-sm">
        <div class="flex-shrink-0">
          <v-button
            :loading="loading"
            @click.prevent="submit"
            class="bg-primary-600 hover:bg-primary-500 text-white px-12 h-12"
          >
            <template
              #title
              v-if="!loading"
              >Access Secure Portal</template
            >
            <template
              #icon
              v-if="!loading"
              class="icon"
            >
              <ion-icon name="arrow-forward-outline"></ion-icon>
            </template>
          </v-button>
        </div>
      </div>

      <div
        class="text-gray-600 text-center mt-4 text-xs"
        v-if="!sent"
      >
        <div class="flex flex-col sm:flex-row justify-center items-center">
          <span class="text-gray-500 p-2">Please check your spam folder. Haven't received it yet?</span>
          <div class="flex">
            <a-link
              href="#"
              class="flex items-center font-normal underline"
              @click.prevent="resend"
            >
              <span class="font-normal">Resend a new code</span>
            </a-link>
            <client-spinner
              class="ml-1"
              v-if="sending"
            />
          </div>
        </div>
      </div>
      <div
        class="flex items-center justify-center text-gray-600 text-center text-xs"
        v-if="sent"
      >
        <span class="flex">
          <ion-icon
            name="checkmark"
            class="text-primary-600 h-5 w-5"
          ></ion-icon>
        </span>
        <span class="font-bold ml-1"> A new verification code has been sent successfully. </span>
      </div>
    </form>
  </div>
</template>

<script>
import Form from "../../../js/helpers/Form"
import ClientSpinner from "../../ClientSpinner.vue"
import ALink from "../../UI/ALink.vue"
import VButton from "../../components/VButton.vue"
import OtpField from "./OtpField.vue"

export default {
  name: "ClientAuthCode",

  props: {
    project: { required: true, type: Object },
    contact: { required: true, type: Object },
    company: { type: Object },
  },

  components: { ALink, ClientSpinner, VButton, OtpField },

  data() {
    return {
      sending: false,
      loading: false,
      sent: false,
      form: new Form({
        code: "",
        remember: true,
        _auth: "",
      }),
      otpValue: "",
    }
  },

  methods: {
    async submit() {
      this.loading = true
      try {
        const { _auth } = this.$route.query

        const { data } = await this.form.post(`/api/client-portal/verification-code/${this.project.id}/verify`, {
          _auth,
        })

        window.location.href = data.data
      } catch (e) {
        console.error(e)
        if (!e.response || e.response.status !== 422) {
          alert("Unable to verify code. Please try again.")
        }
      }
      this.loading = false
    },

    async resend() {
      if (this.sending) {
        return
      }

      this.sending = true

      try {
        const { _auth } = this.$route.query

        await axios.post(`/api/client-portal/verification-code/${this.project.id}`, {
          _auth,
        })

        this.sent = true
      } catch (e) {
        if (!e.response || e.response.status !== 422) {
          alert("Unable to resend code. Please try refreshing the page.")
        }
      }
      this.sending = false
    },

    getPhoneNumber() {
      const phone = this.contact.phone_number

      if (!phone || !phone.mfa_enabled) {
        return null
      }

      return `${phone.phone}`
    },

    onPaste(code) {
      this.form.code = code
      this.submit()
    },
  },
}
</script>

<style scoped></style>
